import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase.js";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import {
  buildPathWithFirstBirthdayTestMode,
  findFirstBirthdayTestProductById,
  getFirstBirthdayTestModeProducts,
  isFirstBirthdayTestModeEnabled,
} from "../utils/firstBirthdayTestMode.js";
import { buildPathWithBranch, isAllahabadBranch, resolveWhatsappNumber } from "../utils/branchWhatsapp.js";
import { markProductRecentlyViewed } from "../utils/listingSessionStorage.js";

const fallbackImage = "/images/placeholder.png";
const SMALL_PLANT_ID = "smallplant"; // ✅ fixed extra item for 1-jar hampers

const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "at",
  "birthday",
  "by",
  "decor",
  "decoration",
  "decorations",
  "for",
  "from",
  "in",
  "of",
  "on",
  "setup",
  "the",
  "theme",
  "to",
  "with",
]);

const slugifyProduct = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");

const tokenizeText = (value) =>
  Array.from(
    new Set(
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/)
        .map((token) => token.trim())
        .filter((token) => token.length > 2 && !STOP_WORDS.has(token))
    )
  );

const normalizeMediaSrc = (src) => {
  const value = String(src || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("/")) return value;
  return `/${value}`;
};

const getMediaLookupKey = (src) => normalizeMediaSrc(src).toLowerCase();

const getPosterFromSourceMap = (map, videoSrc) => {
  if (!map || Array.isArray(map) || typeof map !== "object") return "";

  const direct = map[videoSrc];
  if (direct) return normalizeMediaSrc(direct);

  const normalizedKey = getMediaLookupKey(videoSrc);
  const matchedKey = Object.keys(map).find(
    (key) => getMediaLookupKey(key) === normalizedKey
  );

  return matchedKey ? normalizeMediaSrc(map[matchedKey]) : "";
};

const ProductDetailPage = () => {
  const { slugAndId } = useParams();
  const navigate = useNavigate();
  // slug--id format se ID nikaalo
  const productId = slugAndId?.split("--")[1];
  const isFirstBirthdayLocalTestMode =
    typeof window !== "undefined" &&
    isFirstBirthdayTestModeEnabled(window.location.search, window.location.hostname);
  const locationSearch = typeof window !== "undefined" ? window.location.search : "";
  const isAllahabadVisit = isAllahabadBranch(locationSearch);

  const [groupedData, setGroupedData] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [isViewerVideoPlaying, setIsViewerVideoPlaying] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [similarProducts, setSimilarProducts] = useState([]);

  // ✅ 2️⃣ NEW STATE FOR FEATURES (Platinum state removed)
  const [selectedFeatures, setSelectedFeatures] = useState([]);

  // Return Gifts state
  const [jarSize, setJarSize] = useState("200"); // "200" | "350"
  const [selectedJarCount, setSelectedJarCount] = useState(2); // 1–4
  const [selectedEdibles, setSelectedEdibles] = useState([]); // user-selected edibles only
  const [edibles, setEdibles] = useState([]); // Excel → Firestore

  const isZoomOpenRef = useRef(false);
  const zoomHistoryOpenRef = useRef(false);

  const edibleMap = useMemo(() => {
    const map = {};
    edibles.forEach((e) => {
      if (e.id) map[e.id] = e;
    });
    return map;
  }, [edibles]);

  /* ------------ Firestore se product + edibles fetch ------------ */
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isFirstBirthdayLocalTestMode) {
          const localTestProduct = findFirstBirthdayTestProductById(productId);

          if (localTestProduct) {
            setGroupedData(localTestProduct);
            setSelectedImageIndex(0);
            setIsZoomOpen(false);
            setSimilarProducts(
              getFirstBirthdayTestModeProducts().filter((product) => product.id !== localTestProduct.id)
            );
            setLoading(false);
            return;
          }
        }

        const productSnap = await getDoc(doc(db, "PRODUCTS MAIN", productId));

        if (!productSnap.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const current = {
          id: productSnap.id,
          ...productSnap.data(),
        };

        setGroupedData(current);
        setSelectedImageIndex(0);
        setIsZoomOpen(false);

        const allProductsSnap = await getDocs(collection(db, "PRODUCTS MAIN"));
        setSimilarProducts(
          allProductsSnap.docs
            .map((productDoc) => ({
              id: productDoc.id,
              ...productDoc.data(),
            }))
            .filter((product) => product.id !== current.id)
        );

        // default jar count: Excel se jarCount ya 2
        const defaultJarCount =
          Number(current.jarCount || current.defaultJarCount || 2) || 2;
        setSelectedJarCount(defaultJarCount);
        setSelectedEdibles(Array(defaultJarCount).fill(""));

        if (current.mainCategory === "Return Gifts") {
          const ediblesSnap = await getDocs(collection(db, "RETURN_GIFT_EDIBLES"));
          const edibleDocs = ediblesSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setEdibles(edibleDocs);
        } else {
          setEdibles([]);
        }

        setLoading(false);
      } catch (err) {
        console.error("🔥 Firebase load error:", err);
        setNotFound(true);
        setLoading(false);
      }
    };

    fetchData();
  }, [isFirstBirthdayLocalTestMode, productId]);

  useEffect(() => {
    isZoomOpenRef.current = isZoomOpen;
  }, [isZoomOpen]);

  useEffect(() => {
    if (groupedData) {
      markProductRecentlyViewed(groupedData);
    }
  }, [groupedData]);

  useEffect(() => {
    const handlePopState = () => {
      if (!zoomHistoryOpenRef.current && !isZoomOpenRef.current) {
        return;
      }

      zoomHistoryOpenRef.current = false;
      setIsViewerVideoPlaying(false);
      setIsZoomOpen(false);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  if (loading) return <div className="p-4 text-center">Loading...</div>;
  if (notFound || !groupedData) {
    return (
      <div className="p-4 text-center text-red-600 font-semibold">
        ⚠️ Product not found.
      </div>
    );
  }

  const isReturnGift = groupedData.mainCategory === "Return Gifts";

  // ⭐ Hamper package (Return Gifts vs Decoration)
  let currentPackage = {};
  if (Array.isArray(groupedData.packages) && groupedData.packages.length > 0) {
    // Note: Since Platinum/Gold/Silver selection is removed, we default to first package for logic
    currentPackage = groupedData.packages[0];
  }

  const packageFeatures = currentPackage.features;
  const packageImages = currentPackage.images;
  const packageAltTexts = currentPackage.altTexts;
  const packageVideos = currentPackage.videos;

  const features = packageFeatures || groupedData.features || [];
  const images =
    Array.isArray(packageImages) && packageImages.length > 0
      ? packageImages
      : Array.isArray(groupedData.images)
      ? groupedData.images
      : [];
  const primaryImageCandidates = Array.from(
    new Set(
      [
        ...images,
        currentPackage.image,
        currentPackage.mainImage,
        groupedData.image,
        groupedData.mainImage,
        currentPackage.whatsappImage,
        groupedData.whatsappImage,
      ]
        .map((src) => normalizeMediaSrc(src))
        .filter((src) => src && !/\.mp4|\.webm|\.ogg|\.mov|\.m4v(?:$|[?#])/i.test(src))
    )
  );
  const altTexts =
    Array.isArray(packageAltTexts) && packageAltTexts.length > 0
      ? packageAltTexts
      : Array.isArray(groupedData.altTexts)
      ? groupedData.altTexts
      : [];

  // ✅ NEW: videos array (package ya root se)
  const videos =
    Array.isArray(packageVideos) && packageVideos.length > 0
      ? packageVideos
      : Array.isArray(groupedData.videos)
      ? groupedData.videos
      : [];

  const sharedPosterCandidates = Array.from(
    new Set(
      [
        ...(Array.isArray(currentPackage.videoPosters) ? currentPackage.videoPosters : []),
        ...(Array.isArray(groupedData.videoPosters) ? groupedData.videoPosters : []),
        ...primaryImageCandidates,
      ]
        .map((src) => normalizeMediaSrc(src))
        .filter(Boolean)
    )
  );

  const resolveVideoPoster = (videoSrc, index) => {
    const exactMappedPoster =
      getPosterFromSourceMap(currentPackage.videoPosterMap, videoSrc) ||
      getPosterFromSourceMap(currentPackage.videoPostersBySrc, videoSrc) ||
      getPosterFromSourceMap(groupedData.videoPosterMap, videoSrc) ||
      getPosterFromSourceMap(groupedData.videoPostersBySrc, videoSrc);

    if (exactMappedPoster) return exactMappedPoster;

    const indexedPoster = normalizeMediaSrc(
      currentPackage.videoPosters?.[index] || groupedData.videoPosters?.[index]
    );
    if (indexedPoster) return indexedPoster;

    const perVideoImage = normalizeMediaSrc(images[index]);
    if (perVideoImage) return perVideoImage;

    const primaryCandidate = primaryImageCandidates[index];
    if (primaryCandidate) return primaryCandidate;

    const uniqueFallbackPoster = sharedPosterCandidates[index];
    if (uniqueFallbackPoster) return uniqueFallbackPoster;

    if (primaryImageCandidates.length > 0) {
      return primaryImageCandidates[0];
    }

    if (videos.length === 1 && sharedPosterCandidates.length === 1) {
      return sharedPosterCandidates[0];
    }

    return fallbackImage;
  };

  const mediaItems = Array.from(
    new Map(
      [
        ...images.map((src, index) => ({
          src: normalizeMediaSrc(src),
          type: "image",
          alt:
            altTexts[index] ||
            groupedData.title ||
            `DecorZoom product image ${index + 1}`,
        })),
        ...videos.map((src, index) => ({
          src: normalizeMediaSrc(src),
          type: "video",
          alt: groupedData.title || `DecorZoom product video ${index + 1}`,
          poster: resolveVideoPoster(src, index),
        })),
      ]
        .filter((item) => item.src)
        .map((item) => [item.src, item])
    ).values()
  );

  const safeMediaItems = mediaItems.length
    ? mediaItems
    : [
        {
          src: fallbackImage,
          type: "image",
          alt: groupedData.title || "DecorZoom product",
        },
      ];

  const activeMedia = safeMediaItems[selectedImageIndex] || safeMediaItems[0];

  const whatsappImage =
    currentPackage.whatsappImage ||
    groupedData.whatsappImage ||
    images[0] ||
    null;
  const productLink = `https://www.decorzoom.in/product/${slugAndId}`;

  const jarCount = selectedJarCount || 1;

  // ---------- PRICE / MESSAGE DISPLAY (Decoration packages) ----------
  // ✅ 1️⃣ SEMICOLON FIX
  const priceMsg =
    (currentPackage?.priceMessage &&
      String(currentPackage.priceMessage).trim()) ||
    "";

  const price =
    currentPackage?.price !== undefined && currentPackage?.price !== null
      ? Number(currentPackage.price)
      : null;

  const discounted =
    currentPackage?.discountedPrice !== undefined &&
    currentPackage?.discountedPrice !== null
      ? Number(currentPackage.discountedPrice)
      : null;

  const hasPrice = typeof price === "number" && Number.isFinite(price);
  const hasDisc = typeof discounted === "number" && Number.isFinite(discounted);

  const fallbackText = priceMsg || "Share decoration for pricing";
  // ---------------------------------------------------- //

  // jar-count based base price (temporary main logic) – Return Gifts
  const baseJarPrices = currentPackage.baseJarPrices || {};
  const basePrice = Number(baseJarPrices[String(jarCount)] || 0);

  /* ------------ Price calculation (base + plant + edibles) ------------ */

  let plantPrice = 0;
  const plantEdible = edibleMap[SMALL_PLANT_ID];

  if (jarCount === 1 && plantEdible) {
    plantPrice =
      jarSize === "200"
        ? Number(plantEdible.price200 || 0)
        : Number(plantEdible.price350 || 0);
  }

  let edibleTotal = 0;
  selectedEdibles.forEach((edibleId) => {
    const edible = edibleMap[edibleId];
    if (!edible) return;
    const add =
      jarSize === "200"
        ? Number(edible.price200 || 0)
        : Number(edible.price350 || 0);
    edibleTotal += add;
  });

  let estimatedPrice = null;
  if (isReturnGift && (basePrice > 0 || plantPrice > 0 || edibleTotal > 0)) {
    estimatedPrice = basePrice + plantPrice + edibleTotal;
  }

  /* ------------ WhatsApp message ------------ */

  const buildWhatsappMessage = () => {
    // ✅ 6️⃣ REPLACE DECORATION WHATSAPP MESSAGE
    if (!isReturnGift) {
      let msg = "New Decoration Requirement\n\n";
      msg += `Product: ${groupedData.title}\n\n`;

      if (selectedFeatures.length > 0) {
        msg += "Selected Requirements:\n";
        selectedFeatures.forEach((f, i) => {
          msg += `${i + 1}. ${f}\n`;
        });
      } else {
        msg += "No specific features selected.\n";
      }

      msg += `\nProduct Link: ${productLink}`;
      return msg;
    }

    // Return Gifts → full builder details
    const sizeLabel = jarSize === "200" ? "200 ml" : "350 ml";

    let msg = "New Order from DecorZoom Return Gift Builder\n\n";
    msg += `Hamper Title: ${groupedData.title}\n`;
    msg += `Jar Size: ${sizeLabel}\n`;
    msg += `Jars: ${jarCount}\n`;

    if (basePrice > 0) {
      msg += `Base Hamper (tray + jars + packing): ₹${basePrice}\n`;
    }

    // 🌱 fixed small plant line for 1-jar hamper
    if (jarCount === 1 && plantEdible && plantPrice > 0) {
      msg += `Jar 1: ${plantEdible.label} – ₹${plantPrice}\n`;
    }

    // User-selected edibles
    selectedEdibles.forEach((edibleId, idx) => {
      const edible = edibleMap[edibleId];
      if (!edible) return;
      const priceLine =
        jarSize === "200"
          ? Number(edible.price200 || 0)
          : Number(edible.price350 || 0);

      msg += `Jar ${idx + 1}: ${edible.label} – ₹${priceLine}\n`;
    });

    if (estimatedPrice !== null) {
      msg += `\nTotal Payable: ₹${estimatedPrice}\n`;
    }

    msg += `\nProduct Link: ${productLink}`;

    if (whatsappImage) {
      const imgUrl = whatsappImage.startsWith("http")
        ? whatsappImage
        : `https://www.decorzoom.in${whatsappImage}`;
      msg += `\nImage: ${imgUrl}`;
    }

    msg += `\n\n--- End Order ---`;

    return msg;
  };

  const whatsappMessage = buildWhatsappMessage();

  // 🔥 Final WhatsApp Number (All Categories → Kiran Only)
  const whatsappNumber = resolveWhatsappNumber("918377985949", locationSearch);

  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
    whatsappMessage
  )}`;

  const descriptionText =
    groupedData.description ||
    (groupedData?.features?.length
      ? groupedData.features.join(", ")
      : groupedData?.title
      ? `Buy ${groupedData.title} online at best price from DecorZoom.`
      : "Premium decorations and return gifts from DecorZoom.");

  const primaryImage =
    images && images.length ? images[0] : "https://www.decorzoom.in/images/logo.png";
  const absolutePrimaryImage = primaryImage.startsWith("http")
    ? primaryImage
    : `https://www.decorzoom.in${primaryImage}`;

  const similarProductList = !groupedData || !similarProducts.length
    ? []
    : similarProducts
        .map((product) => {
          const baseTitleTokens = tokenizeText(groupedData.title);
          const baseCategory = String(groupedData.mainCategory || "").toLowerCase();
          const baseSubcategory = String(
            groupedData.subcategory || groupedData.subCategory || ""
          ).toLowerCase();
          const baseProductType = String(groupedData.productType || "").toLowerCase();
          const productTitleTokens = tokenizeText(product.title);
          const overlapCount = productTitleTokens.filter((token) =>
            baseTitleTokens.includes(token)
          ).length;
          const sameCategory =
            String(product.mainCategory || "").toLowerCase() === baseCategory;
          const sameSubcategory =
            String(product.subcategory || product.subCategory || "").toLowerCase() ===
            baseSubcategory;
          const sameProductType =
            String(product.productType || "").toLowerCase() === baseProductType;
          const firstPackage = Array.isArray(product.packages)
            ? product.packages[0] || {}
            : {};

          return {
            ...product,
            score:
              (sameSubcategory ? 6 : 0) +
              (sameCategory ? 4 : 0) +
              (sameProductType ? 2 : 0) +
              overlapCount * 2,
            thumb:
              firstPackage.images?.[0] ||
              product.images?.[0] ||
              fallbackImage,
            productPrice: Number(
              firstPackage.discountedPrice ??
                firstPackage.price ??
                product.discountedPrice ??
                product.price ??
                0
            ),
          };
        })
        .filter((product) => product.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 12);

  const getBreadcrumbPath = () => {
    const mainCategory = (groupedData.mainCategory || "").toLowerCase();
    const subcategory = (groupedData.subcategory || groupedData.subCategory || "").toLowerCase();

    if (mainCategory === "first birthday decorations") {
      if (subcategory === "home decor") {
        return {
          name: "Home Decor",
          url: "https://www.decorzoom.in/subcategory/home-decor",
        };
      }

      if (subcategory === "cafe") {
        return {
          name: "Cafe Decor",
          url: "https://www.decorzoom.in/subcategory/cafe-decor",
        };
      }

      return {
        name: "First Birthday Decorations",
        url: "https://www.decorzoom.in/subcategory/first-birthday-decorations",
      };
    }

    const slug =
      (groupedData.subcategory || groupedData.subCategory || groupedData.mainCategory || "")
        .toLowerCase()
        .replace(/\s+/g, "-");

    return {
      name:
        groupedData.subcategory ||
        groupedData.subCategory ||
        groupedData.mainCategory ||
        "DecorZoom",
      url: `https://www.decorzoom.in/subcategory/${slug}`,
    };
  };

  const breadcrumbPath = getBreadcrumbPath();

  const shareProduct = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`,
      "_blank"
    );
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const openZoomViewer = () => {
    setIsViewerVideoPlaying(false);

    if (!isZoomOpenRef.current && !zoomHistoryOpenRef.current) {
      window.history.pushState(
        {
          ...(window.history.state || {}),
          dzProductZoomViewer: true,
        },
        "",
        window.location.href
      );
      zoomHistoryOpenRef.current = true;
    }

    setIsZoomOpen(true);
  };

  const closeZoomViewer = () => {
    setIsViewerVideoPlaying(false);

    if (zoomHistoryOpenRef.current && window.history.state?.dzProductZoomViewer) {
      window.history.back();
      return;
    }

    zoomHistoryOpenRef.current = false;
    setIsZoomOpen(false);
  };

  // Jar count change → edibles array ka size adjust karo
  const handleJarCountChange = (count) => {
    setSelectedJarCount(count);
    setSelectedEdibles((prev) => {
      const next = [...prev];
      if (count > next.length) {
        while (next.length < count) next.push("");
      } else if (count < next.length) {
        next.length = count;
      }
      return next;
    });
  };

  const handleEdibleChange = (index, value) => {
    const next = [...selectedEdibles];
    next[index] = value;
    setSelectedEdibles(next);
  };

  // ✅ 3️⃣ ADD THIS FUNCTION
  const toggleFeature = (feature) => {
    setSelectedFeatures((prev) =>
      prev.includes(feature)
        ? prev.filter((f) => f !== feature)
        : [...prev, feature]
    );
  };

  const showPrevMedia = () => {
    setIsViewerVideoPlaying(false);
    setSelectedImageIndex((prev) =>
      prev === 0 ? safeMediaItems.length - 1 : prev - 1
    );
  };

  const showNextMedia = () => {
    setIsViewerVideoPlaying(false);
    setSelectedImageIndex((prev) =>
      prev === safeMediaItems.length - 1 ? 0 : prev + 1
    );
  };

  /* =================== RENDER =================== */

  return (
    <>
      <Helmet>
        <title>
          {groupedData?.title
            ? `${groupedData.title} | DecorZoom`
            : "DecorZoom"}
        </title>

        <meta
          name="description"
          content={descriptionText.slice(0, 160)}
        />

        <link
          rel="canonical"
          href={`https://www.decorzoom.in/product/${slugAndId}`}
        />

        <meta property="og:title" content={groupedData?.title || "DecorZoom"} />
        <meta
          property="og:description"
          content={descriptionText.slice(0, 200)}
        />
        <meta property="og:image" content={absolutePrimaryImage} />
        <meta
          property="og:url"
          content={`https://www.decorzoom.in/product/${slugAndId}`}
        />
        <meta property="og:type" content="product" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: groupedData.title,
            description: descriptionText,
            image: images.map((img) =>
              img.startsWith("http") ? img : `https://www.decorzoom.in${img}`
            ),
            sku: groupedData.ID || productId,
            brand: {
              "@type": "Brand",
              name: "DecorZoom",
            },
            category: groupedData.subcategory || groupedData.mainCategory || "Decor",
            url: `https://www.decorzoom.in/product/${slugAndId}`,
            offers:
              hasDisc || hasPrice
                ? {
                    "@type": "Offer",
                    priceCurrency: "INR",
                    price: hasDisc && discounted < price ? discounted : price,
                    availability: "https://schema.org/InStock",
                    url: `https://www.decorzoom.in/product/${slugAndId}`,
                  }
                : undefined,
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.decorzoom.in/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: breadcrumbPath.name,
                item: breadcrumbPath.url,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: groupedData.title,
                item: `https://www.decorzoom.in/product/${slugAndId}`,
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {isFirstBirthdayLocalTestMode && groupedData?.mainCategory === "First Birthday Decorations" && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
            First Birthday TEST MODE is active on localhost. This product is being loaded from the local test dataset.
          </div>
        )}

        {/* Back button */}
        <button
          onClick={handleBack}
          className="text-green-600 font-semibold hover:underline mb-4 flex items-center gap-1 text-sm sm:text-base"
        >
          ← Back
        </button>

        <div className="grid grid-cols-1 gap-6 items-start lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
          {/* LEFT: Main media gallery */}
          <div className="space-y-3">
            <div className="rounded-[24px] border border-gray-200 bg-white p-2.5 shadow-sm sm:p-4">
              <div className="overflow-hidden rounded-[20px] bg-gray-100">
                <div className="relative flex h-[240px] items-center justify-center sm:h-[340px] lg:h-[440px]">
                  {activeMedia.type === "video" ? (
                    <button
                      type="button"
                      onClick={openZoomViewer}
                      className="group relative h-full w-full"
                    >
                      <img
                        src={activeMedia.poster || fallbackImage}
                        alt={activeMedia.alt}
                        loading="eager"
                        decoding="async"
                        className="h-full w-full bg-gray-100 object-contain"
                      />
                      <div className="absolute inset-0 bg-black/20 transition group-hover:bg-black/30" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-2xl text-gray-900 shadow-lg">
                          &#9654;
                        </span>
                      </div>
                      <div className="absolute bottom-3 right-3 rounded-full bg-black/65 px-3 py-1 text-[11px] font-semibold text-white">
                        Tap to play
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={openZoomViewer}
                      className="h-full w-full cursor-zoom-in"
                    >
                      <img
                        src={activeMedia.src}
                        alt={activeMedia.alt}
                        loading="eager"
                        decoding="async"
                        className="h-full w-full bg-white object-contain"
                      />
                    </button>
                  )}
                </div>
              </div>

              {safeMediaItems.length > 1 && (
                <div className="mt-3">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-gray-900">Product media</div>
                    <div className="text-xs text-gray-500">
                      {selectedImageIndex + 1} / {safeMediaItems.length}
                    </div>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {safeMediaItems.map((media, index) => (
                      <button
                        key={`${media.src}-${index}`}
                        type="button"
                        onClick={() => {
                          setIsViewerVideoPlaying(false);
                          setSelectedImageIndex(index);
                        }}
                        className={`relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl border bg-white sm:h-16 sm:w-16 ${
                          index === selectedImageIndex
                            ? "border-green-500 ring-2 ring-green-200"
                            : "border-gray-200"
                        }`}
                      >
                        {media.type === "video" ? (
                          <>
                            <img
                              src={media.poster || fallbackImage}
                              alt={media.alt}
                              loading="lazy"
                              decoding="async"
                              className="h-full w-full object-cover"
                            />
                            <span className="absolute inset-0 bg-black/20" />
                            <span className="absolute inset-0 flex items-center justify-center text-lg text-white drop-shadow-md">
                              &#9654;
                            </span>
                          </>
                        ) : (
                          <img
                            src={media.src}
                            alt={media.alt}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Title + Package / Hamper controls / Features / Buttons */}
          <div className="space-y-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
              {groupedData.title}
            </h1>

            {groupedData.description && (
              <p className="text-sm sm:text-base text-gray-700 leading-7">
                {groupedData.description}
              </p>
            )}

            {/* ⭐ Decoration products → Price & Requirements */}
            {!isReturnGift && (
              <>
                {/* Price block */}
                <div className="text-lg space-y-1">
                  {hasDisc && hasPrice && discounted < price ? (
                    <>
                      <p className="text-red-500 line-through">
                        Rs. {price.toLocaleString("en-IN")}
                      </p>
                      <p className="text-green-600 font-bold text-xl">
                        Rs. {discounted.toLocaleString("en-IN")}
                      </p>
                    </>
                  ) : hasPrice ? (
                    <p className="text-green-600 font-bold text-xl">
                      Rs. {price.toLocaleString("en-IN")}
                    </p>
                  ) : (
                    <p className="text-green-700 font-semibold text-lg">
                      {fallbackText}
                    </p>
                  )}
                </div>

                {/* ✅ 4️⃣ & 5️⃣ REPLACE FEATURES UI BLOCK (Platinum buttons removed) */}
                {features && features.length > 0 && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h2 className="font-bold text-md mb-3">
                      Select Your Requirement & Share
                    </h2>

                    <div className="space-y-2">
                      {features.map((f, idx) => (
                        <label
                          key={idx}
                          className="flex items-center gap-2 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFeatures.includes(f)}
                            onChange={() => toggleFeature(f)}
                            className="w-4 h-4 accent-green-600"
                          />
                          {f}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* 🎁 Return Gifts → Jar builder controls */}
            {isReturnGift && (
              <div className="space-y-4">
                {/* Jar size toggle */}
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                  <p className="text-sm font-semibold mb-2">Select Jar Size</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setJarSize("200")}
                      className={`py-2 rounded-full font-semibold text-sm sm:text-base border ${
                        jarSize === "200"
                          ? "bg-green-600 text-white border-green-600"
                          : "bg-white text-gray-700 border-green-300"
                      }`}
                    >
                      200 ml Jars
                    </button>
                    <button
                      onClick={() => setJarSize("350")}
                      className={`py-2 rounded-full font-semibold text-sm sm:text-base border ${
                        jarSize === "350"
                          ? "bg-green-600 text-white border-green-600"
                          : "bg-white text-gray-700 border-green-300"
                      }`}
                    >
                      350 ml Jars
                    </button>
                  </div>
                </div>

                {/* Jar count select */}
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                  <p className="text-sm font-semibold mb-2">Select Jar Count</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map((count) => (
                      <button
                        key={count}
                        onClick={() => handleJarCountChange(count)}
                        className={`py-2 rounded-full font-semibold text-sm border ${
                          jarCount === count
                            ? "bg-green-600 text-white border-green-600"
                            : "bg-white text-gray-700 border-green-300"
                        }`}
                      >
                        {count} Jar{count > 1 ? "s" : ""}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Edibles per jar */}
                <div className="bg-white border border-green-100 rounded-2xl p-4 shadow-sm">
                  <p className="text-sm font-semibold mb-3">
                    Select Edibles for Each Jar
                  </p>

                  <div className="space-y-3">
                    {jarCount === 1 ? (
                      <>
                        {/* 🔒 Fixed small plant row */}
                        <div className="grid grid-cols-[60px,1fr] gap-2 items-center">
                          <span className="text-xs sm:text-sm text-gray-600">
                            Jar 1
                          </span>
                          <div className="flex items-center justify-between border border-dashed border-green-300 rounded-lg px-3 py-2 text-xs sm:text-sm bg-green-50">
                            <span className="text-gray-700 font-semibold">
                              {plantEdible?.label || "Small Plant"} (Fixed)
                            </span>
                            {plantPrice > 0 && (
                              <span className="text-green-700 font-bold">
                                ₹{plantPrice}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* User-select edible for same jar */}
                        <div className="grid grid-cols-[60px,1fr] gap-2 items-center">
                          <span className="text-xs sm:text-sm text-gray-600">
                            Jar 1
                          </span>
                          <select
                            value={selectedEdibles[0] || ""}
                            onChange={(e) => handleEdibleChange(0, e.target.value)}
                            className="w-full border border-green-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="">Select edible</option>
                            {[...edibles]
                              .filter((ed) => ed.id !== SMALL_PLANT_ID)
                              .sort((a, b) => {
                                const pa =
                                  jarSize === "200"
                                    ? Number(a.price200 || 999999)
                                    : Number(a.price350 || 999999);
                                const pb =
                                  jarSize === "200"
                                    ? Number(b.price200 || 999999)
                                    : Number(b.price350 || 999999);
                                return pa - pb;
                              })
                              .map((ed) => (
                                <option key={ed.id} value={ed.id}>
                                  {ed.label}
                                  {jarSize === "200" && ed.price200
                                    ? ` (₹${ed.price200})`
                                    : ""}
                                  {jarSize === "350" && ed.price350
                                    ? ` (₹${ed.price350})`
                                    : ""}
                                </option>
                              ))}
                          </select>
                        </div>
                      </>
                    ) : (
                      // 🔁 Multi-jar case (no small plant)
                      Array.from({ length: jarCount }).map((_, idx) => {
                        const currentSelectedId = selectedEdibles[idx] || "";

                        const usedIds = new Set(
                          selectedEdibles.filter(
                            (id, i) => i !== idx && id && id.trim() !== ""
                          )
                        );

                        const sortedAvailable = [...edibles]
                          .filter(
                            (ed) =>
                              ed.id !== SMALL_PLANT_ID && !usedIds.has(ed.id)
                          )
                          .sort((a, b) => {
                            const pa =
                              jarSize === "200"
                                ? Number(a.price200 || 999999)
                                : Number(a.price350 || 999999);
                            const pb =
                              jarSize === "200"
                                ? Number(b.price200 || 999999)
                                : Number(b.price350 || 999999);
                            return pa - pb;
                          });

                        let finalOptions = sortedAvailable;
                        if (
                          currentSelectedId &&
                          !sortedAvailable.find((e) => e.id === currentSelectedId)
                        ) {
                          const selectedObj = edibles.find(
                            (e) => e.id === currentSelectedId
                          );
                          if (selectedObj) {
                            finalOptions = [selectedObj, ...sortedAvailable];
                          }
                        }

                        return (
                          <div
                            key={idx}
                            className="grid grid-cols-[60px,1fr] gap-2 items-center"
                          >
                            <span className="text-xs sm:text-sm text-gray-600">
                              Jar {idx + 1}
                            </span>
                            <select
                              value={currentSelectedId}
                              onChange={(e) =>
                                handleEdibleChange(idx, e.target.value)
                              }
                              className="w-full border border-green-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                              <option value="">Select edible</option>
                              {finalOptions.map((ed) => (
                                <option key={ed.id} value={ed.id}>
                                  {ed.label}
                                  {jarSize === "200" && ed.price200
                                    ? ` (₹${ed.price200})`
                                    : ""}
                                  {jarSize === "350" && ed.price350
                                    ? ` (₹${ed.price350})`
                                    : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <p className="mt-3 text-xs text-gray-500">
                    Select edibles to see estimated hamper price (base hamper +
                    all jars).
                  </p>

                  {/* Estimated price */}
                  {isReturnGift && estimatedPrice !== null && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-sm sm:text-base flex items-center justify-between">
                      <span className="font-semibold text-gray-700">
                        Estimated hamper price (all jars)
                      </span>
                      <span className="font-bold text-green-700">
                        ₹{estimatedPrice.toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}

                  {/* Jar icons – small plant + selected edibles */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {/* plant icon for 1-jar */}
                    {jarCount === 1 && plantEdible && (
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border border-green-200">
                        <img
                          src={plantEdible.image || fallbackImage}
                          alt={plantEdible.altText || plantEdible.label}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    {selectedEdibles
                      .map((edibleId, idx) => {
                        const edible = edibleMap[edibleId];
                        const thumbSrc =
                          edible?.image || edible?.thumbImage || fallbackImage;
                        const alt =
                          edible?.altText ||
                          edible?.label ||
                          `Jar ${idx + 1}`;
                        return { idx, thumbSrc, alt, hasEdible: !!edible };
                      })
                      .filter((x) => x.hasEdible)
                      .map((x) => (
                        <div
                          key={x.idx}
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border border-green-200"
                        >
                          <img
                            src={x.thumbSrc}
                            alt={x.alt}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* General Features (for non-decor or fallback) */}
            {isReturnGift && features && features.length > 0 && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h2 className="font-bold text-md mb-2">Features:</h2>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-800">
                  {features.map((f, idx) => (
                    <li key={idx}>{f}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Share / WhatsApp */}
            <button
              onClick={shareProduct}
              className="inline-block w-full text-center bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg text-lg font-bold mt-4"
            >
              Share this product
            </button>

            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full text-center bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg text-lg font-bold mt-3"
            >
              🔓 Unlock My Special Price on WhatsApp
            </a>
          </div>
        </div>

        {similarProductList.length > 0 && (
          <section className="mt-8 sm:mt-10">
            <div className="mb-3">
              <h2 className="text-lg font-bold text-gray-900">Similar products</h2>
              <p className="text-sm text-gray-500">
                Related options based on this theme, category, and product type.
              </p>
            </div>

            <div className="flex gap-3 overflow-x-auto pb-2">
              {similarProductList.map((product) => {
                const productSlug = product.slug || slugifyProduct(product.title);
                const similarHref = buildPathWithBranch(
                  buildPathWithFirstBirthdayTestMode(
                    `/product/${productSlug}--${product.id}`,
                    isFirstBirthdayLocalTestMode &&
                      product.mainCategory === "First Birthday Decorations"
                  ),
                  isAllahabadVisit
                );

                return (
                  <article
                    key={product.id}
                    onClick={() => navigate(similarHref)}
                    className="w-[150px] shrink-0 cursor-pointer overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md sm:w-[170px]"
                  >
                    <div className="aspect-square bg-gray-100">
                      <img
                        src={product.thumb}
                        alt={product.title || "DecorZoom product"}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.src = fallbackImage;
                        }}
                      />
                    </div>
                    <div className="p-3">
                      <div className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-snug text-gray-900">
                        {product.title}
                      </div>
                      <div className="mt-1 line-clamp-1 text-[11px] text-gray-500">
                        {product.subcategory || product.subCategory || product.mainCategory}
                      </div>
                      {product.productPrice > 0 && (
                        <div className="mt-2 text-sm font-bold text-green-700">
                          Rs. {product.productPrice.toLocaleString("en-IN")}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {isZoomOpen && (
          <div className="fixed inset-0 z-50 bg-black/90">
            <button
              className="absolute right-4 top-4 z-10 rounded-full bg-white/10 px-3 py-2 text-sm font-semibold text-white backdrop-blur"
              onClick={closeZoomViewer}
            >
              Close
            </button>
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between px-4 py-4 text-white">
                <div className="min-w-0 pr-3">
                  <div className="truncate text-sm font-semibold">{groupedData.title}</div>
                  <div className="text-xs text-white/70">
                    {selectedImageIndex + 1} / {safeMediaItems.length}
                  </div>
                </div>
              </div>

              <div className="relative flex min-h-0 flex-1 items-center justify-center px-3 pb-3 sm:px-6 sm:pb-6">
                {safeMediaItems.length > 1 && (
                  <button
                    className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-2xl text-white backdrop-blur"
                    onClick={showPrevMedia}
                  >
                    &#8249;
                  </button>
                )}

                <div className="flex h-full w-full max-w-5xl items-center justify-center overflow-hidden rounded-[24px] bg-black">
                  {activeMedia.type === "video" ? (
                    isViewerVideoPlaying ? (
                      <video
                        key={`zoom-${activeMedia.src}`}
                        controls
                        playsInline
                        autoPlay
                        preload="metadata"
                        poster={activeMedia.poster || fallbackImage}
                        className="max-h-full w-full object-contain"
                      >
                        <source src={activeMedia.src} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsViewerVideoPlaying(true)}
                        className="group relative h-full w-full"
                      >
                        <img
                          src={activeMedia.poster || fallbackImage}
                          alt={activeMedia.alt}
                          className="h-full w-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/20 transition group-hover:bg-black/30" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-3xl text-gray-900 shadow-xl">
                            &#9654;
                          </span>
                        </div>
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/65 px-4 py-2 text-sm font-semibold text-white">
                          Play video
                        </div>
                      </button>
                    )
                  ) : (
                    <img
                      src={activeMedia.src}
                      alt={activeMedia.alt || "Zoomed View"}
                      className="max-h-full max-w-full object-contain"
                    />
                  )}
                </div>

                {safeMediaItems.length > 1 && (
                  <button
                    className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-2xl text-white backdrop-blur"
                    onClick={showNextMedia}
                  >
                    &#8250;
                  </button>
                )}
              </div>

              {safeMediaItems.length > 1 && (
                <div className="flex gap-2 overflow-x-auto px-3 pb-4 sm:px-5">
                  {safeMediaItems.map((media, idx) => (
                    <button
                      key={`zoom-thumb-${media.src}-${idx}`}
                      onClick={() => {
                        setIsViewerVideoPlaying(false);
                        setSelectedImageIndex(idx);
                      }}
                      className={`relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl border ${
                        idx === selectedImageIndex
                          ? "border-green-400 ring-2 ring-green-300/50"
                          : "border-white/15"
                      }`}
                    >
                      {media.type === "video" ? (
                        <>
                          <img
                            src={media.poster || fallbackImage}
                            alt={media.alt}
                            className="h-full w-full object-cover"
                          />
                          <span className="absolute inset-0 bg-black/20" />
                          <span className="absolute inset-0 flex items-center justify-center text-lg text-white drop-shadow-md">
                            &#9654;
                          </span>
                        </>
                      ) : (
                        <img
                          src={media.src}
                          alt={media.alt}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Image zoom overlay */}
        {false && isZoomOpen && (
          <div className="fixed inset-0 z-50 bg-black/90">
            <button
              className="absolute right-4 top-4 z-10 rounded-full bg-white/10 px-3 py-2 text-sm font-semibold text-white backdrop-blur"
              onClick={() => setIsZoomOpen(false)}
            >
              Close
            </button>
            <button
              className="absolute left-4 text-white text-3xl"
              onClick={() =>
                setSelectedImageIndex((prev) =>
                  prev === 0 ? images.length - 1 : prev - 1
                )
              }
            >
              ⬅️
            </button>
            <img
              src={images[selectedImageIndex] || fallbackImage}
              alt={altTexts[selectedImageIndex] || "Zoomed View"}
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            />
            <button
              className="absolute right-4 text-white text-3xl"
              onClick={() =>
                setSelectedImageIndex((prev) =>
                  prev === images.length - 1 ? 0 : prev + 1
                )
              }
            >
              ➡️
            </button>

            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
              {images.map((_, idx) => (
                <div
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`w-3 h-3 rounded-full cursor-pointer ${
                    idx === selectedImageIndex ? "bg-green-500" : "bg-white/30"
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductDetailPage;
